<template>
    <li :id="item.domId" class="tw:relative tw:list-none" :data-nav-key="item.key">
        <!-- Toggle con submenú -->
        <template v-if="hasChildren">
            <button
                type="button"
                class="admin-nav-hover tw:relative tw:flex tw:items-center tw:gap-2.5 tw:w-full tw:py-[5px] tw:leading-4 tw:rounded-lg tw:font-medium tw:transition-[padding,background-color,color] tw:duration-200 tw:ease-linear tw:group tw:hover:text-white"
                :class="[collapsed ? 'tw:px-3.5' : 'tw:px-2.5', active ? 'tw:text-white' : 'tw:text-slate-300']"
                :aria-label="item.label"
                :data-testid="'sidebar-menu-' + item.key"
                @click="$emit('toggle', item.key)"
            >
                <i
                    :class="[
                        item.icon,
                        'tw:shrink-0 tw:text-center tw:transition-colors tw:group-hover:text-white',
                        collapsed ? 'tw:w-5 tw:text-[16px]' : 'tw:w-4',
                        active ? 'tw:text-white' : 'tw:text-slate-400'
                    ]"
                    aria-hidden="true"
                ></i>
                <transition name="admin-label">
                    <span v-if="labelsVisible" class="tw:flex-1 tw:truncate tw:text-left">{{ item.label }}</span>
                </transition>
                <transition name="admin-label">
                    <i
                        v-if="labelsVisible"
                        class="admin-chevron fas fa-chevron-down tw:shrink-0 tw:text-[10px]"
                        :class="[expanded ? 'tw:rotate-180' : '', active ? 'tw:text-white' : 'tw:text-slate-400']"
                        aria-hidden="true"
                    ></i>
                </transition>
                <span v-if="collapsed && item.badge > 0" class="tw:absolute tw:top-1 tw:right-1 tw:w-2 tw:h-2 tw:rounded-full tw:bg-rose-400" aria-hidden="true"></span>
            </button>

            <transition name="admin-submenu">
                <div v-if="expanded && !collapsed" class="admin-submenu-panel">
                    <ul
                        class="tw:mt-1 tw:ml-4 tw:pl-3 tw:pr-1 tw:py-0.5 tw:space-y-1 tw:border-l tw:border-slate-800 tw:list-none tw:m-0"
                        data-testid="sidebar-submenu"
                    >
                        <li v-for="child in item.children" :key="child.key" class="tw:list-none">
                            <router-link
                                :to="child.route"
                                :class="childLinkClasses(child)"
                                :aria-label="child.label"
                                :data-testid="'sidebar-menu-' + child.key"
                                @click.native="$emit('navigate')"
                            >
                                <i :class="[child.icon, 'tw:w-3.5 tw:shrink-0 tw:text-center']" aria-hidden="true"></i>
                                <span class="tw:truncate">{{ child.label }}</span>
                            </router-link>
                        </li>
                    </ul>
                </div>
            </transition>
        </template>

        <!-- Item acción (ej. Salir): no es una ruta -->
        <a
            v-else-if="item.action"
            href="#"
            class="tw:group tw:relative tw:flex tw:items-center tw:gap-2.5 tw:py-[5px] tw:leading-4 tw:rounded-lg tw:font-medium tw:transition-[padding,background-color,color] tw:duration-200 tw:ease-linear"
            :class="[
                collapsed ? 'tw:px-3.5' : 'tw:px-2.5',
                item.danger
                    ? 'tw:text-slate-400 tw:hover:text-rose-400 tw:hover:bg-slate-900/90'
                    : 'tw:text-slate-300 tw:hover:text-white tw:hover:bg-slate-900/90'
            ]"
            :aria-label="item.label"
            :data-testid="'sidebar-menu-' + item.key"
            @click.prevent="$emit('action', item.action)"
        >
            <i
                :class="[
                    item.icon,
                    'tw:shrink-0 tw:text-center tw:transition-colors',
                    collapsed ? 'tw:w-5 tw:text-[16px]' : 'tw:w-4',
                    item.danger ? 'tw:text-slate-500 tw:group-hover:text-rose-400' : 'tw:text-slate-400 tw:group-hover:text-white'
                ]"
                aria-hidden="true"
            ></i>
            <transition name="admin-label">
                <span v-if="labelsVisible" class="tw:flex-1 tw:truncate">{{ item.label }}</span>
            </transition>
        </a>

        <!-- Item simple -->
        <router-link
            v-else
            :to="item.route"
            class="tw:group tw:relative tw:flex tw:items-center tw:gap-2.5 tw:py-2 tw:rounded-lg tw:transition-[padding,background-color,color] tw:duration-200 tw:ease-linear"
            :class="linkClasses"
            :aria-label="item.label"
            :data-testid="'sidebar-menu-' + item.key"
            @click.native="$emit('navigate')"
        >
            <i :class="[item.icon, iconClasses]" aria-hidden="true"></i>
            <transition name="admin-label">
                <span v-if="labelsVisible" class="tw:flex-1 tw:truncate">{{ item.label }}</span>
            </transition>
            <span
                v-if="labelsVisible && item.badge > 0"
                class="tw:inline-flex tw:items-center tw:justify-center tw:min-w-[1.25rem] tw:h-5 tw:px-1.5 tw:rounded-full tw:text-[10px] tw:font-bold tw:text-rose-300 tw:bg-rose-500/20 tw:border tw:border-rose-500/30"
                data-testid="sidebar-badge"
            >{{ item.badge }}</span>
            <span v-if="collapsed && item.badge > 0" class="tw:absolute tw:top-1 tw:right-1 tw:w-2 tw:h-2 tw:rounded-full tw:bg-rose-400" aria-hidden="true"></span>
        </router-link>
    </li>
</template>

<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        item: { type: Object, required: true },
        collapsed: { type: Boolean, default: false },
        labelsVisible: { type: Boolean, default: true },
        activeKeys: { type: Object, default: () => ({}) },
        expandedKeys: { type: Object, default: () => ({}) }
    });

    defineEmits(['toggle', 'navigate', 'action']);

    const hasChildren = computed(() => !!(props.item.children && props.item.children.length));
    const active = computed(() => !!props.activeKeys[props.item.key]);
    const expanded = computed(() => !!props.expandedKeys[props.item.key]);

    // Estilos de club vía clases CSS con var: evita el choque con el
    // !important de Tailwind (los style inline perderían) y es verificable
    // en unit tests (jsdom descarta var()/color-mix() en styles inline).
    const SUB_BASE_CLASSES = 'tw:flex tw:items-center tw:gap-2.5 tw:px-2.5 tw:py-1.5 tw:rounded-md tw:border tw:text-[13px] tw:font-medium tw:transition-colors';
    const SUB_IDLE_CLASSES = 'admin-nav-hover tw:border-transparent tw:text-slate-400 tw:hover:text-white';
    const SUB_ACTIVE_CLASSES = 'tw:border-transparent tw:bg-[var(--admin-club-color)] tw:text-[color:var(--admin-club-fg)] tw:font-semibold tw:shadow-sm';

    const linkClasses = computed(() => {
        const base = 'tw:flex tw:items-center tw:gap-2.5 tw:py-[5px] tw:leading-4 tw:rounded-lg tw:transition-[padding,background-color,color] tw:duration-200 tw:ease-linear tw:group tw:relative';
        const padding = props.collapsed ? 'tw:px-3.5' : 'tw:px-2.5';
        if (active.value) {
            return [base, padding, 'tw:bg-[var(--admin-club-color)] tw:text-[color:var(--admin-club-fg)] tw:font-semibold tw:shadow-sm'].join(' ');
        }
        return [base, padding, 'admin-nav-hover tw:text-slate-300 tw:font-medium tw:hover:text-white'].join(' ');
    });

    const iconClasses = computed(() => {
        const base = 'tw:shrink-0 tw:text-center tw:transition-colors';
        const size = props.collapsed ? 'tw:w-5 tw:text-[16px]' : 'tw:w-4';
        if (active.value) return [base, size].join(' ');
        return [base, size, 'tw:text-slate-400 tw:group-hover:text-white'].join(' ');
    });

    function isChildActive(child) {
        return !!props.activeKeys[child.key];
    }

    function childLinkClasses(child) {
        const stateClasses = isChildActive(child) ? SUB_ACTIVE_CLASSES : SUB_IDLE_CLASSES;
        return SUB_BASE_CLASSES + ' ' + stateClasses;
    }
</script>

<style scoped>
    /*
     * Hover como versión suave del ítem activo: tinte del color del club en
     * lugar de un slate plano. El rgba de arriba es el fallback para
     * navegadores sin color-mix.
     */
    .admin-nav-hover:hover {
        background-color: rgba(148, 163, 184, 0.14);
        background-color: color-mix(in srgb, var(--admin-club-color) 35%, transparent);
    }

    .admin-label-enter-active,
    .admin-label-leave-active {
        transition: opacity 0.12s ease;
    }

    .admin-label-enter,
    .admin-label-leave-to {
        opacity: 0;
    }

    .admin-chevron {
        transition: opacity 0.12s ease, rotate 0.2s ease, transform 0.2s ease;
    }

    .admin-submenu-panel {
        display: grid;
        grid-template-rows: 1fr;
        overflow: hidden;
    }

    .admin-submenu-panel > ul {
        min-height: 0;
        overflow: hidden;
    }

    .admin-submenu-enter-active,
    .admin-submenu-leave-active {
        transition: grid-template-rows 0.25s ease, opacity 0.2s ease;
    }

    .admin-submenu-enter,
    .admin-submenu-leave-to {
        grid-template-rows: 0fr;
        opacity: 0;
    }

    .admin-submenu-enter-to,
    .admin-submenu-leave {
        grid-template-rows: 1fr;
        opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
        .admin-submenu-enter-active,
        .admin-submenu-leave-active {
            transition: none;
        }
    }
</style>
